import React, { useCallback, useMemo } from 'react';
import {
    GivelifyButton,
    GivelifyForm,
    GivelifyFormCheckbox,
    GivelifyFormTextField,
    GivelifyLabel,
    GivelifyNotification,
    GivelifyLink,
} from '@givelify/ui';
import * as Yup from 'yup';
import { useTranslation } from 'react-i18next';
import { useFormContext } from 'react-hook-form';
import {
    CheckboxWrapper,
    GivelifyLogo,
    Labels,
    Paper,
    SubmitButtonWrapper,
    TextFieldWrapper,
    Wrapper,
} from './styles';
import { webConfig } from '../../../../webConfig';
import { useMediaQuery, useTheme } from '@mui/material';
import { useInvokeApi } from '../../utils/useInvokeApi';
import ActivateSuccess from '../Activate/view';

const termsLink = webConfig.givelifyTermsLink;

interface FormProps {
    password: string;
    passwordConfirm: string;
    agreeTerms: boolean;
}

const AcceptInvite: React.FC<{ name: string; token: string }> = ({
    name,
    token,
}) => {
    const { t } = useTranslation();
    const TRANSLATION_KEY = 'signup.acceptInvite';
    const scopedTranslate = useCallback(
        (key: string, prs?: any) => t(`${TRANSLATION_KEY}.${key}`, prs),
        [t],
    );

    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('mobile'));

    const copy = useMemo(
        () => ({
            requiredPassword: scopedTranslate('requiredPassword'),
            shortPassword: scopedTranslate('shortPassword'),
            errorPassword: scopedTranslate('errorPassword'),
            requiredConfirmPassword: scopedTranslate('requiredConfirmPassword'),
            matchedPassword: scopedTranslate('matchedPassword'),
            title1: scopedTranslate('title1', { name }),
            title2: scopedTranslate('title2'),
            passwordPlaceholder: scopedTranslate('passwordPlaceholder'),
            confirmPasswordPlaceholder: scopedTranslate(
                'confirmPasswordPlaceholder',
            ),
            terms1: scopedTranslate('terms1'),
            terms2: scopedTranslate('terms2'),
            submitText: scopedTranslate('submitText'),
            409: scopedTranslate('error.409'),
            410: scopedTranslate('error.410'),
            404: scopedTranslate('error.404'),
            422: scopedTranslate('error.422'),
            common: scopedTranslate('error.common'),
        }),
        [scopedTranslate, name],
    );

    const activateInviteErrorMessage = useCallback(
        (code) =>
            (code === 409 && copy['409']) ||
            (code === 410 && copy['410']) ||
            (code === 404 && copy['404']) ||
            (code === 422 && copy['422']) ||
            copy.common,
        [copy],
    );

    const validationSchema = Yup.object<FormProps>({
        password: Yup.string()
            .required(copy.requiredPassword)
            .min(6, copy.shortPassword)
            .matches(/^(?=.*[A-Z])(?=.*\d).*$/, {
                message: copy.errorPassword,
                excludeEmptyString: true,
            }),
        passwordConfirm: Yup.string()
            .required(copy.requiredConfirmPassword)
            .oneOf([Yup.ref('password'), null], copy.matchedPassword),
        agreeTerms: Yup.boolean().required().oneOf([true]),
    });

    const [activateInviteResponse, activateInviteRequest] = useInvokeApi();
    if (activateInviteResponse.type === 'REQUEST_SUCCESS')
        return <ActivateSuccess />;

    return (
        <GivelifyForm<FormProps>
            schema={validationSchema}
            onSubmit={(data) => {
                activateInviteRequest('POST', '/invite/activation', {
                    invitationCode: token,
                    password: data.password,
                });
            }}
            defaultValues={{
                password: '',
                passwordConfirm: '',
                agreeTerms: false,
            }}
        >
            <Wrapper>
                <Paper data-testid="acceptForm">
                    <GivelifyLogo />
                    {activateInviteResponse.type === 'REQUEST_ERROR' && (
                        <div
                            data-testid={`error${activateInviteResponse.error.response?.status}`}
                        >
                            <GivelifyNotification
                                show
                                variant="error"
                                text={activateInviteErrorMessage(
                                    activateInviteResponse.error.response
                                        ?.status,
                                )}
                            />
                        </div>
                    )}
                    <Labels>
                        <GivelifyLabel
                            text={copy.title1}
                            variant={isMobile ? 'body2B' : 'body1B'}
                        />{' '}
                        <GivelifyLabel
                            text={copy.title2}
                            variant={isMobile ? 'body2B' : 'body1B'}
                        />
                    </Labels>
                    <TextFieldWrapper>
                        <GivelifyFormTextField<FormProps>
                            name="password"
                            fullWidth
                            placeholder={copy.passwordPlaceholder}
                            type="password"
                            inputProps={{
                                'data-testid': 'passwordInput',
                            }}
                        />
                    </TextFieldWrapper>
                    <TextFieldWrapper>
                        <GivelifyFormTextField<FormProps>
                            name="passwordConfirm"
                            fullWidth
                            placeholder={copy.confirmPasswordPlaceholder}
                            type="password"
                            inputProps={{
                                'data-testid': 'passwordConfirmInput',
                            }}
                        />
                    </TextFieldWrapper>
                    <CheckboxWrapper>
                        <GivelifyFormCheckbox<FormProps>
                            name="agreeTerms"
                            inputProps={{
                                'data-testid': 'agreeTermsCheckbox',
                            }}
                        />
                        <GivelifyLabel variant={'body1'}>
                            {copy.terms1}{' '}
                            <GivelifyLink
                                href={termsLink}
                                target="_blank"
                                text={copy.terms2}
                                variant="medium"
                            />
                        </GivelifyLabel>
                    </CheckboxWrapper>
                    <SubmitButton
                        title={copy.submitText}
                        isLoading={
                            activateInviteResponse.type === 'REQUEST_START'
                        }
                    />
                </Paper>
            </Wrapper>
        </GivelifyForm>
    );
};

const SubmitButton: React.FC<{ title: string; isLoading: boolean }> = ({
    title,
    isLoading,
}) => {
    const {
        formState: { isValid, isDirty, isSubmitting },
    } = useFormContext<FormProps>();

    const canSubmit = isValid && isDirty && !isSubmitting && !isLoading;
    return (
        <>
            <SubmitButtonWrapper>
                <GivelifyButton
                    type="submit"
                    text={title}
                    variant="primary"
                    disabled={!canSubmit}
                    data-testid="submitButton"
                    isLoading={isLoading}
                />
            </SubmitButtonWrapper>
        </>
    );
};

export default AcceptInvite;
